import {
  EnhancedBillingPage,
  PageBillingType,
} from '@mm/state/interfaces/connected-page';
import { PlanSubtypes } from '@mm/state/interfaces/subscription';
import React from 'react';
import {
  useAdjustedPricingBillingPlan,
  useMatchBillingPlan,
} from '../hooks/use-match-billing-plan';
import { BadgeTheme, MonkeyBadge } from './MonkeyBadge/MonkeyBadge';

export const BADGE_DEFINITIONS = {
  [PageBillingType.Free]: {
    theme: BadgeTheme.gray,
    planText: 'Free',
  },
  [PageBillingType.Promotion]: {
    theme: BadgeTheme.green,
    planText: 'Promo',
  },
  [PageBillingType.TrialTeam]: {
    theme: BadgeTheme.green,
    planText: 'Trial',
  },
  [PageBillingType.LegacyPro]: {
    theme: BadgeTheme.lightOrange,
    planText: 'PRO Legacy',
  },
  [PageBillingType.Pro]: {
    theme: BadgeTheme.lightPurple,
    planText: 'Operator',
  },
  [PageBillingType.Pro2023]: {
    theme: BadgeTheme.unicornGradient,
    planText: 'Pro',
  },
  [PageBillingType.FreePlus]: {
    theme: BadgeTheme.green,
    planText: 'Free Plus',
  },
  [PageBillingType.Starter]: {
    theme: BadgeTheme.starterGradient,
    planText: 'Starter',
  },
  [PageBillingType.ProUnicorn]: {
    theme: BadgeTheme.unicornGradient,
    planText: 'Startup',
  },
  [PageBillingType.Team]: {
    theme: BadgeTheme.greenGradient,
    planText: 'Team 2019',
  },
  [PageBillingType.TeamLabel]: {
    theme: BadgeTheme.greenGradient,
    planText: 'Agency',
  },
  [PageBillingType.NewTeam]: {
    theme: BadgeTheme.greenGradient,
    planText: 'Agency',
  },
  [PageBillingType.LegacyTeam]: {
    theme: BadgeTheme.lightOrange,
    planText: 'Team Legacy',
  },
  [PageBillingType.CustomTeam]: {
    theme: BadgeTheme.lightPurple,
    planText: 'Enterprise',
  },
  [PageBillingType.CustomPro]: {
    theme: BadgeTheme.lightBlueToGreenGradient,
    planText: 'Custom',
  },
  [PageBillingType.WP]: {
    theme: BadgeTheme.lightBlueToGreenGradient,
    planText: 'WordPress',
  },
  [PageBillingType.Instachamp]: {
    theme: BadgeTheme.pinkGradient,
    planText: 'InstaChamp',
  },
  [PageBillingType.SpecialPro]: {
    theme: BadgeTheme.lightOrange,
    planText: 'Special',
  },
  [PageBillingType.SpecialTeam]: {
    theme: BadgeTheme.lightOrange,
    planText: 'Special',
  },
};

type Props = {
  className?: string;
  facebookPage?: EnhancedBillingPage;
  billingType: PageBillingType;
  hideFreeIcon?: boolean;
  size?: number;
};

export const PlanBadge = ({
  className,
  facebookPage,
  billingType,
  hideFreeIcon,
  size,
}: Props) => {
  const getModifiedBillingType = () => {
    if (
      facebookPage?.subscription?.planSubtypes.includes(
        PlanSubtypes.starter2023,
      )
    ) {
      return PageBillingType.Starter;
    }
    if (
      facebookPage?.subscription?.planSubtypes.includes(PlanSubtypes.free2023)
    ) {
      return PageBillingType.FreePlus;
    }
    if (
      facebookPage?.subscription?.planSubtypes.includes(PlanSubtypes.pro2023)
    ) {
      return PageBillingType.Pro2023;
    }

    return billingType;
  };
  const modifiedBillingType = getModifiedBillingType();
  const { theme, planText } = BADGE_DEFINITIONS[modifiedBillingType];
  if (planText === 'FREE' && hideFreeIcon) {
    return null;
  }
  return (
    <MonkeyBadge className={className} theme={theme} size={size}>
      {planText}
    </MonkeyBadge>
  );
};

// performs current plan check, hides if match or higher
export const RequiredPlanBadge = ({ billingType, ...rest }: Props) => {
  const adjustedBillingType = useAdjustedPricingBillingPlan(billingType);
  const match = useMatchBillingPlan(adjustedBillingType);
  return match ? null : (
    <PlanBadge billingType={adjustedBillingType} {...rest} />
  );
};

export const PlanCircle = ({
  className,
  billingType,
  hideFreeIcon,
  size,
}: Props) => {
  const { theme, planText } = BADGE_DEFINITIONS[billingType];
  if (planText === 'FREE' && hideFreeIcon) {
    return null;
  }
  const style = {
    height: `${size}px`,
    width: `${size}px`,
    borderRadius: '50%',
  };

  return <MonkeyBadge className={className} theme={theme} style={style} />;
};

export const PlanText = ({
  className,
  billingType,
  hideFreeIcon,
  size,
}: Props) => {
  const { theme, planText } = BADGE_DEFINITIONS[billingType];
  if (planText === 'FREE' && hideFreeIcon) {
    return null;
  }
  const style: React.CSSProperties = {
    backgroundClip: 'text',
    color: 'transparent',
    WebkitBackgroundClip: 'text',
  };
  return (
    <MonkeyBadge
      className={className}
      theme={theme}
      size={size}
      style={style}
      unwrappedChildren
    >
      {planText}
    </MonkeyBadge>
  );
};
